import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { ClientOptionsModel, CreateProjectModel, CustomProjectModel, ProjectType } from 'core/domain/project/model/projectModel';
import { getProjects } from 'services/api/projects';
import { DefaultOptionType } from 'antd/lib/select';
import {
  TABLE_PAGINATION_PAGE_DEFAULT,
  TABLE_PAGINATION_SIZE_ALL_RESULTS,
  TABLE_PAGINATION_SIZE_DEFAULT,
  TablePaginationSearchValue,
} from 'constants/table';
import { getProjectsWithPaginationPath, getProjectPath } from 'components/pages/App/routes/projects/config';
import { hasPermission } from 'services/permissions';
import { useProfileUser } from 'hooks/useProfileUser';
import { ProjectTab } from 'models/project.model';
import { UserRoleType } from 'models/users.model';
import { ProjectCreateFormDataProps, ProjectCreateFormValues } from 'components/molecules/ProjectCreateForm';
import { getDeleteProjectErrorText, projectTypeTranslationManager } from 'utils/projects';
import { checkEmailListErrors, EMAIL_LIST_SEPARATORS } from 'utils/email';
import { GetClientsList } from 'core/domain/client/repositories/getClientsList';
import { MessageErrorModel, useMessage } from 'hooks/useMessage';
import { CreateProject } from 'core/domain/project/repositories/createProject';
import { useEnvironment } from 'hooks/useEnvironment';
import { useUserSession } from 'hooks/useUserSession';
import { DeleteProject } from 'core/domain/project/repositories/deleteProject';
import { DELAY_MESSAGE_TIME } from 'constants/time';
import { GetUserVerifierListByClientId } from 'core/domain/users/repositories/getUserVerifierListByClientId';
import { UserVerifierModel } from 'core/domain/users/models';
import { ProjectsColumns } from '../../../organisms/ProjectListColumns';

const DEFAULT_PAGE_NUMBER = Number(TABLE_PAGINATION_PAGE_DEFAULT);
const DEFAULT_PAGE_SIZE_NUMBER = Number(TABLE_PAGINATION_SIZE_DEFAULT);

export const useProjects = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { setMessageError, setMessageSuccess } = useMessage();
  const { host } = useEnvironment();
  const { token } = useUserSession();
  const { data: userData } = useProfileUser();
  const page = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.PAGE);
  const size = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.SIZE);
  const [projects, setProjects] = useState<CustomProjectModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  const [currentPage, setCurrentPage] = useState<number>(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  const [pageSize, setPageSize] = useState<number>(Number(TABLE_PAGINATION_SIZE_DEFAULT));
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isNotificationModalVisible, setIsNotificationModalVisible] = useState<boolean>(false);
  const [isCreateProjectModalVisible, setIsCreateProjectModalVisible] = useState<boolean>(false);
  const [form] = Form.useForm<ProjectCreateFormValues>();
  const [isCreatingProject, setIsCreatingProject] = useState<boolean>(false);
  const [clientOptions, setClientOptions] = useState<ClientOptionsModel[]>([]);
  const [isClientSelectLoading, setIsClientSelectLoading] = useState<boolean>(false);
  const [isDeleteProjectModalVisible, setIsDeleteProjectModalVisible] = useState<boolean>(false);
  const [deleteProjectId, setDeleteProjectId] = useState<string>('');
  const [isDeletingProject, setIsDeletingProject] = useState<boolean>(false);
  const [userVerifierList, setUserVerifierList] = useState<DefaultOptionType[]>([]);
  const [isLoadingUserVerifierList, setIsLoadingUserVerifierList] = useState<boolean>(false);
  const [isUsersRequireVerificationChecked, setIsUsersRequireVerificationChecked] = useState<boolean>(false);

  const isUserSuperAdmin = !!userData?.roles.includes(UserRoleType.ROLE_ASSETS_MANAGER_SUPER_ADMIN);

  const hasNotificationPermission = hasPermission('send_community_notifications');

  const translations = {
    projects: t('projects'),
    notify: t('notify_a_community'),
    createProjectButtonText: t('_PROJECTS_LIST_CREATE_PROJECT_BUTTON_TEXT'),
  };

  const createProjectModalTranslations = {
    title: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_TITLE_PLACEHOLDER'),
    actionButton: t('create'),
    cancelButton: t('cancel'),
  };

  const createProjectFormTranslations = {
    nameLabel: t('name'),
    namePlaceholder: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_NAME_PLACEHOLDER'),
    nameError: t('required'),
    supportEmailsLabel: t('support_email'),
    supportEmailsPlaceholder: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_SUPPORT_EMAIL_LIST_PLACEHOLDER'),
    usersVerifiersLabel: t('registration_validator_user_info'),
    usersVerifiersPlaceholder: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_USER_VERIFIER_LIST_PLACEHOLDER'),
    usersRequireVerificationLabel: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_USER_VERIFIER_LIST_LABEL'),
    typeLabel: t('_PROJECT_TYPE'),
    typeError: t('required'),
    typePlaceholder: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_TYPE_PLACEHOLDER'),
    clientLabel: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_CLIENT_LABEL'),
    clientPlaceholder: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_CLIENT_PLACEHOLDER'),
    clientError: t('required'),
    userVerifierListError: t('required'),
  };

  const deleteProjectModalTranslations = {
    title: t('_DELETE_PROJECT_MODAL_TITLE'),
    text: t('_DELETE_PROJECT_MODAL_TEXT'),
    mainButtonText: t('delete'),
    secondaryButtonText: t('cancel'),
  };

  const columnTranslations = {
    nameTitle: t('name'),
    typeTitle: t('type'),
    supportEmailTitle: t('support_email'),
    actionsTitle: t('actions'),
    actionsDeleteProjectButtonText: t('_PROJECTS_LIST_DELETE_PROJECT_TEXT'),
    notReportedText: t('not_reported'),
  };

  const getProjectsWithPagination = async ({ page, size, search }: { page: number; size: number; search?: string }): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await getProjects({ search, page: { current: page, size } });
      const parsedProjectsList: CustomProjectModel[] = response.data.map((project: any) => ({
        id: project.id,
        logo: project.logo,
        name: project.name,
        supportEmail: project.support_email,
        type: project.type,
      }));
      response && setProjects(parsedProjectsList);
      response && setTotalPages(response.meta.total_pages);
    } catch (error) {
      setMessageError({ description: t('_PROJECTS_LIST_ERROR_MESSAGE') });
    } finally {
      setIsLoading(false);
    }
  };

  const onChangePage = (page: number, pageSize?: number) => {
    setCurrentPage(page);
    const parsedPageSize = pageSize || Number(TABLE_PAGINATION_SIZE_DEFAULT);
    setPageSize(parsedPageSize);
    history.push(getProjectsWithPaginationPath({ page: String(page), size: String(parsedPageSize) }));
  };

  const onResetPage = () => {
    setCurrentPage(DEFAULT_PAGE_NUMBER);
    setPageSize(DEFAULT_PAGE_SIZE_NUMBER);
    getProjectsWithPagination({
      page: Number(page) || Number(TABLE_PAGINATION_PAGE_DEFAULT),
      size: Number(size) || Number(TABLE_PAGINATION_SIZE_DEFAULT),
      search: searchQuery,
    });
  };

  const onSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  };

  const onSelectProject = (project: CustomProjectModel) => {
    history.push(
      getProjectPath({
        projectId: project.id,
        tab: ProjectTab.ASSETS,
        page: TABLE_PAGINATION_PAGE_DEFAULT,
        size: TABLE_PAGINATION_SIZE_DEFAULT,
      })
    );
  };

  const onOpenNotification = () => {
    setIsNotificationModalVisible(true);
  };

  const onCloseNotification = () => {
    setIsNotificationModalVisible(false);
  };

  const getClients = async () => {
    setIsClientSelectLoading(true);
    try {
      const data = await GetClientsList();
      if (!!data?.length) {
        const clients = data.map(({ id, name }) => ({ label: name, value: id }));
        setClientOptions(clients);
      }
    } catch {
      setMessageError({ description: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_CLIENT_ERROR') });
    } finally {
      setIsClientSelectLoading(false);
    }
  };

  const onOpenCreateProjectModal = () => {
    setIsCreateProjectModalVisible(true);
    getClients();
  };

  const getTypeOptions = () => {
    return Object.values(ProjectType).map((option) => {
      return { label: projectTypeTranslationManager[option], value: option };
    });
  };

  const onCloseCreateProjectModal = () => {
    setIsCreateProjectModalVisible(false);
    form.resetFields();
    setUserVerifierList([]);
    setIsUsersRequireVerificationChecked(false);
  };

  const onCreateProject = async (values: ProjectCreateFormValues) => {
    const { client, name, supportEmails, type, usersRequireVerification, usersVerifiers } = values;
    try {
      setIsCreatingProject(true);
      const data: CreateProjectModel = {
        clientId: client?.value || '',
        name,
        supportEmail: supportEmails,
        type,
        usersRequireVerification: usersRequireVerification || false,
        usersVerifiers,
      };
      await CreateProject({ host, token, data });
      setMessageSuccess({ description: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_SUCCESS_MESSAGE') });
      onResetPage();
      onCloseCreateProjectModal();
    } catch (error) {
      setMessageError({ description: t('_PROJECTS_LIST_CREATE_PROJECT_ERROR_MESSAGE') });
    } finally {
      setIsCreatingProject(false);
    }
  };

  const onCreateProjectFailed = () => {
    setIsCreatingProject(false);
  };

  const parseUserVerifierList = (userList: UserVerifierModel[]) => {
    return userList.map(({ userEmail, userId }) => ({ label: userEmail, value: userId }));
  };

  const onSelectClientForm = async (id: string) => {
    try {
      setIsLoadingUserVerifierList(true);
      const { data } = await GetUserVerifierListByClientId({
        host,
        token,
        clientId: id,
        page: Number(TABLE_PAGINATION_PAGE_DEFAULT),
        size: Number(TABLE_PAGINATION_SIZE_ALL_RESULTS),
      });
      const parsedUserVerifierList = parseUserVerifierList(data.userList);
      setUserVerifierList(parsedUserVerifierList);
    } catch (error) {
      setMessageError({ description: t('_PROJECTS_LIST_USER_VERIFIER_LIST_ERROR_MESSAGE') });
    } finally {
      setIsLoadingUserVerifierList(false);
    }
  };

  const onChangeUserVerificationCheck = (value: boolean) => {
    setIsUsersRequireVerificationChecked(value);
  };

  const createProjectFormData: ProjectCreateFormDataProps = {
    allowedUsersToVerifyAccounts: userVerifierList,
    form,
    loading: isCreatingProject,
    onFinish: onCreateProject,
    onFinishFailed: onCreateProjectFailed,
    translations: createProjectFormTranslations,
    typeOptions: getTypeOptions(),
    validateEmailFormat: checkEmailListErrors,
    onSelectClient: onSelectClientForm,
    onChangeUserVerificationCheck,
    supportEmailsSeparators: EMAIL_LIST_SEPARATORS,
    showClientOptions: true,
    clientOptions,
    isClientSelectLoading,
    isLoadingUserVerifierList,
    isUsersRequireVerificationChecked,
  };

  const onOpenDeleteProjectModal = (id: string) => {
    setIsDeleteProjectModalVisible(true);
    setDeleteProjectId(id);
  };

  const onCloseDeleteProjectModal = () => {
    setIsDeleteProjectModalVisible(false);
    setDeleteProjectId('');
  };

  const columns = ProjectsColumns({
    translations: columnTranslations,
    onOpenDeleteProjectModal,
    isUserSuperAdmin,
  });

  const onDeleteProject = async () => {
    try {
      setIsDeletingProject(true);
      await DeleteProject({ host, token, projectId: deleteProjectId });
      setMessageSuccess({ description: t('_PROJECTS_LIST_DELETE_PROJECT_FORM_SUCCESS_MESSAGE') });
      onResetPage();
      onCloseDeleteProjectModal();
    } catch (error) {
      const description = getDeleteProjectErrorText(error as MessageErrorModel);
      setMessageError({ description }, DELAY_MESSAGE_TIME.MEDIUM);
    } finally {
      setIsDeletingProject(false);
    }
  };

  useEffect(() => {
    const newPage = page || TABLE_PAGINATION_PAGE_DEFAULT;
    const newSize = size || TABLE_PAGINATION_SIZE_DEFAULT;
    setCurrentPage(Number(newPage));
    setPageSize(Number(newSize));
  }, [page, size]);

  useEffect(() => {
    getProjectsWithPagination({
      page: Number(page) || Number(TABLE_PAGINATION_PAGE_DEFAULT),
      size: Number(size) || Number(TABLE_PAGINATION_SIZE_DEFAULT),
      search: searchQuery,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, searchQuery]);

  return {
    projects,
    columns,
    totalPages,
    currentPage,
    pageSize,
    translations,
    createProjectModalTranslations,
    createProjectFormData,
    deleteProjectModalTranslations,
    onChangePage,
    onSearch,
    onSelectProject,
    onOpenNotification,
    onCloseNotification,
    onOpenCreateProjectModal,
    onCloseCreateProjectModal,
    onCloseDeleteProjectModal,
    onDeleteProject,
    loadingProjects: isLoading,
    hasNotificationPermission,
    notificationModalVisible: isNotificationModalVisible,
    isUserSuperAdmin,
    isCreateProjectModalVisible,
    isDeleteProjectModalVisible,
    isDeletingProject,
    isLoadingUserVerifierList,
  };
};
